import type { GroupAnalysisSpanData } from '@/generated/types/navigator/types.gen';
import startCase from 'lodash/startCase';
import { useUserClientContext } from '@/app/clientContext';
import { Insight } from '@/views/map/Insight/Insight';
import { useFetchNetworkGroupsQuery } from '@/features/networkGroupSlice';
import { useFormatDistance } from '@/helpers/distanceMetric';
import { Section } from '@/views/list/level3/Section';
import { formatPhasing, useVoltageFormatter } from '@/views/list/Level3Item';

export const NetworkTab = (a: GroupAnalysisSpanData) => {
  const { context } = useUserClientContext();
  const voltageFormatter = useVoltageFormatter();
  const voltageInKv = voltageFormatter(a.voltageInKv ?? undefined);
  const { data: networkGroups } = useFetchNetworkGroupsQuery();
  const spanLength = useFormatDistance(
    a.length ?? undefined,
    context.analysis_config.metric === 'ft' ? 'mile' : 'kilometer',
    0,
    0
  );
  const rowWidthField = a.bufferInFt;
  const rowWidth = useFormatDistance(
    rowWidthField ? rowWidthField * 2 : undefined,
    context.analysis_config.metric === 'ft' ? 'foot' : 'meter',
    0,
    0,
    context.analysis_config.metric === 'm'
  );

  return (
    <Section name="Network">
      {a.phasingType && a.network === 1 && (
        <Insight name="Phase" data={formatPhasing(a.phasingType)} />
      )}
      {a.voltageInKv && <Insight name="Voltage" data={voltageInKv} />}

      {networkGroups?.map((group) => {
        const raw = a[group.name];
        const value =
          typeof raw === 'string'
            ? raw
            : group.name === 'span'
              ? a.level3
              : undefined;

        return (
          value && (
            <Insight
              name={startCase(group.singularTitle ?? '')}
              key={group.name}
              data={value}
            />
          )
        );
      })}
      <Insight name="Span length" data={spanLength} />
      {a.bufferInFt && <Insight name="Right of way width" data={rowWidth} />}
      <Insight name="Overstory ID" data={a.level3} withCopy />
    </Section>
  );
};
